import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import * as Icons from 'lucide-react';
import {
  Search,
  Plus,
  FileText,
  ChevronLeft,
  ChevronRight,
  Users,
  Globe,
  FolderOpen,
  MoreVertical,
  Eye,
  Trash2,
  Power,
  PowerOff,
  X,
  List,
  LayoutGrid,
  ArrowDownUp,
  ArrowUpAZ,
  ArrowDownZA,
  ArrowUp01,
  ArrowDown10,
  CalendarArrowUp,
  CalendarArrowDown,
  Check,
  Share2,
  Edit,
  UserPlus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useKnowledgeBase } from '../contexts/KnowledgeBaseContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { CreateKnowledgeBaseDialog } from '../components/knowledgeBase/CreateKnowledgeBaseDialog';
import { ShareKnowledgeBaseModal } from '../components/knowledgeBase/ShareKnowledgeBaseModal';
import { KnowledgeBase } from '../services/knowledgeBaseService';
import { knowledgeBaseService } from '../services/knowledgeBaseService';
import { canShareKnowledgeBase } from '../utils/knowledgeBasePermissions';
import { fileAPI } from '../services/api/fileApi';
import { ProgressiveCoverImage } from '../components/ProgressiveImage';
import { formatDateWithYear } from '../utils/dateFormat';

type SortField = 'name' | 'updated' | 'created' | 'documents';
type SortOrder = 'asc' | 'desc';

const SORT_FIELD_DEFAULTS: Record<SortField, SortOrder> = {
  name: 'asc',
  updated: 'desc',
  created: 'desc',
  documents: 'desc',
};

const KnowledgeBasePage: React.FC = () => {
  const { t } = useTranslation();
  const {
    state,
    fetchKnowledgeBases,
    fetchInactiveKnowledgeBases,
    fetchAllKnowledgeBases,
    setGlobalFilter: setContextGlobalFilter,
  } = useKnowledgeBase();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Derive sort state from URL params, default to 'name' with 'asc'
  const sortField: SortField = (searchParams.get('sort') as SortField) || 'name';
  const sortOrder: SortOrder =
    (searchParams.get('order') as SortOrder) || SORT_FIELD_DEFAULTS[sortField];

  // Adaptive pagination based on screen width
  const getPerPage = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1536) return 15; // 2xl screens
      if (window.innerWidth >= 1280) return 12; // xl screens
      if (window.innerWidth >= 1024) return 10; // lg screens
      if (window.innerWidth >= 768) return 8; // md screens
      return 6; // sm and xs screens
    }
    return 10; // default for SSR
  };

  const [pagination, setPagination] = useState({
    myKb: { page: 1, perPage: getPerPage() },
    shared: { page: 1, perPage: getPerPage() },
    public: { page: 1, perPage: getPerPage() },
  });
  // Global filter state (applies to all sections)
  // Use global filter from context, but initialize local state for backward compatibility
  const [globalFilter, setLocalGlobalFilter] = useState<'all' | 'recent' | 'inactive'>(
    state.globalFilter as 'all' | 'recent' | 'inactive'
  );

  // Sync local globalFilter with context
  useEffect(() => {
    // If context returns 'default', map it to 'all'
    if (state.globalFilter === 'default') {
      setLocalGlobalFilter('all');
    } else {
      setLocalGlobalFilter(state.globalFilter as 'all' | 'recent' | 'inactive');
    }
  }, [state.globalFilter]);

  // Wrapper to update both local state and context
  const setGlobalFilter = useCallback(
    (filter: 'all' | 'recent' | 'inactive') => {
      setLocalGlobalFilter(filter);
      setContextGlobalFilter(filter);
    },
    [setContextGlobalFilter]
  );

  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  // Handle sort change with toggle behavior
  const handleSortChange = (field: SortField) => {
    const newParams = new URLSearchParams(searchParams);
    let newOrder: SortOrder;

    if (sortField === field) {
      // Toggle direction if clicking the same field
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Use default order for new field
      newOrder = SORT_FIELD_DEFAULTS[field];
    }

    newParams.set('sort', field);
    newParams.set('order', newOrder);
    setSearchParams(newParams);
  };

  // Get the appropriate sorting icon based on field and order
  const getSortIcon = () => {
    switch (sortField) {
      case 'name':
        return sortOrder === 'asc' ? ArrowUpAZ : ArrowDownZA;
      case 'documents':
        return sortOrder === 'asc' ? ArrowUp01 : ArrowDown10;
      case 'updated':
      case 'created':
        return sortOrder === 'asc' ? CalendarArrowUp : CalendarArrowDown;
      default:
        return ArrowDownUp;
    }
  };

  const SortIcon = getSortIcon();
  const [showShareModal, setShowShareModal] = useState(false);
  const [kBToShare, setKBToShare] = useState<KnowledgeBase | null>(null);
  const [activateDialogOpen, setActivateDialogOpen] = useState<{
    kbId: string;
    kbName: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [kbToEdit, setKbToEdit] = useState<KnowledgeBase | null>(null);
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [kbToDelete, setKbToDelete] = useState<KnowledgeBase | null>(null);

  // Note: fetchKnowledgeBases is already called in KnowledgeBaseContext provider
  // No need to call it here to avoid infinite re-renders

  // Handle knowledge base click
  const handleKnowledgeBaseClick = (kbId: string) => {
    const kb = state.knowledgeBases.find(k => k.id === kbId);
    if (kb && !kb.is_active) {
      setActivateDialogOpen({ kbId, kbName: kb.name });
    } else {
      navigate(`/knowledge/${kbId}/files`);
    }
  };

  // Handle activate knowledge base
  const handleActivateKB = async (kbId: string) => {
    const result = await knowledgeBaseService.activateKnowledgeBase(kbId);
    if (result) {
      toast.success(t('knowledge.activation.success', { name: result.name }));
      // Refresh based on current filter
      if (globalFilter === 'inactive') {
        await fetchInactiveKnowledgeBases();
      } else {
        await fetchKnowledgeBases();
      }
      setActivateDialogOpen(null);
      // Navigate to files page after activation
      navigate(`/knowledge/${kbId}/files`);
    } else {
      toast.error(t('knowledge.activation.error'));
    }
  };

  // Handle deactivate knowledge base
  const handleDeactivateKB = async (kbId: string) => {
    const result = await knowledgeBaseService.deactivateKnowledgeBase(kbId);
    if (result) {
      toast.success(t('knowledge.deactivation.success', { name: result.name }));
      // Refresh based on current filter
      if (globalFilter === 'inactive') {
        await fetchInactiveKnowledgeBases();
      } else {
        await fetchKnowledgeBases();
      }
      setActionMenuOpen(null);
    } else {
      toast.error(t('knowledge.deactivation.error'));
    }
  };

  // Handle global filter change
  const handleGlobalFilterChange = async (filter: typeof globalFilter) => {
    setGlobalFilter(filter);
    const perPage = getPerPage();
    setPagination({
      myKb: { page: 1, perPage },
      shared: { page: 1, perPage },
      public: { page: 1, perPage },
    });

    // Fetch appropriate data based on filter
    if (filter === 'inactive') {
      await fetchInactiveKnowledgeBases();
    } else if (filter === 'all') {
      await fetchAllKnowledgeBases();
    } else {
      await fetchKnowledgeBases();
    }
  };

  // Handle knowledge base creation
  const handleCreateKnowledgeBase = (knowledgeBase: any) => {
    // Refresh the knowledge base list
    fetchKnowledgeBases();
    // Show success message or navigate to the new knowledge base
    console.log('Knowledge base created:', knowledgeBase);
  };

  // Handle knowledge base edit
  const handleEditKnowledgeBase = (knowledgeBase: KnowledgeBase) => {
    setKbToEdit(knowledgeBase);
    setEditDialogOpen(true);
    setActionMenuOpen(null);
  };

  // Handle knowledge base edit success
  const handleEditSuccess = async (knowledgeBase: KnowledgeBase) => {
    toast.success(t('knowledge.dialog.editSuccess', { name: knowledgeBase.name }));
    // Refresh the knowledge base list
    if (globalFilter === 'inactive') {
      await fetchInactiveKnowledgeBases();
    } else {
      await fetchKnowledgeBases();
    }
  };

  // Handle knowledge base delete click
  const handleDeleteClick = (knowledgeBase: KnowledgeBase) => {
    setKbToDelete(knowledgeBase);
    setDeleteConfirmOpen(true);
    setActionMenuOpen(null);
  };

  // Handle knowledge base delete confirm
  const handleDeleteConfirm = async () => {
    if (!kbToDelete) return;

    const success = await knowledgeBaseService.deleteKnowledgeBase(kbToDelete.id);
    if (success) {
      toast.success(t('knowledge.actions.deleteSuccess', { name: kbToDelete.name }));
      // Refresh the knowledge base list
      if (globalFilter === 'inactive') {
        await fetchInactiveKnowledgeBases();
      } else {
        await fetchKnowledgeBases();
      }
    } else {
      toast.error(t('knowledge.actions.deleteFailed'));
    }
    setDeleteConfirmOpen(false);
    setKbToDelete(null);
  };

  // Helper function to check if KB was recently updated (within 7 days)
  const isRecentlyUpdated = (updatedAt: string): boolean => {
    const daysSinceUpdate = Math.floor(
      (Date.now() - new Date(updatedAt).getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysSinceUpdate <= 7;
  };

  // Helper function to get share count
  const getShareCount = (kb: KnowledgeBase): number => {
    // If API provides share_count, use it; otherwise calculate from shares array
    if (kb.share_count !== undefined) {
      return kb.share_count;
    }
    return kb.shares?.length || 0;
  };

  // Filter knowledge bases based on search query
  const filteredKnowledgeBases = useMemo(
    () =>
      state.knowledgeBases.filter(
        kb =>
          kb.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (kb.description && kb.description.toLowerCase().includes(searchQuery.toLowerCase()))
      ),
    [state.knowledgeBases, searchQuery]
  );

  // Helper function to apply filters and sorting (using global filter/sort)
  const applyFiltersAndSort = (kbs: typeof filteredKnowledgeBases) => {
    let filtered = kbs;

    // Apply filter
    if (globalFilter === 'recent') {
      filtered = filtered.filter(kb => isRecentlyUpdated(kb.updated_at));
    }
    // Note: 'inactive' filter is handled by backend API call, no client-side filtering needed
    // Note: 'all' filter shows everything

    // Apply sorting with field and order
    filtered = [...filtered].sort((a, b) => {
      const orderMultiplier = sortOrder === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'name':
          return orderMultiplier * a.name.localeCompare(b.name);
        case 'updated':
          return (
            orderMultiplier * (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime())
          );
        case 'created':
          return (
            orderMultiplier * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
          );
        case 'documents':
          return orderMultiplier * (a.document_count - b.document_count);
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Group and filter knowledge bases by categories
  const myKnowledgeBasesAll = useMemo(
    () => applyFiltersAndSort(filteredKnowledgeBases.filter(kb => kb.owner_id === user?.id)),
    [filteredKnowledgeBases, user?.id, globalFilter, sortField, sortOrder]
  );
  const sharedKnowledgeBasesAll = useMemo(
    () =>
      applyFiltersAndSort(
        filteredKnowledgeBases.filter(
          kb => kb.shares && kb.shares.some(share => share.shared_with_user_id === user?.id)
        )
      ),
    [filteredKnowledgeBases, user?.id, globalFilter, sortField, sortOrder]
  );
  const publicKnowledgeBasesAll = useMemo(
    () => applyFiltersAndSort(filteredKnowledgeBases.filter(kb => kb.is_public)),
    [filteredKnowledgeBases, globalFilter, sortField, sortOrder]
  );

  // Paginate each section
  const myKnowledgeBases = useMemo(() => {
    const start = (pagination.myKb.page - 1) * pagination.myKb.perPage;
    return myKnowledgeBasesAll.slice(start, start + pagination.myKb.perPage);
  }, [myKnowledgeBasesAll, pagination.myKb]);

  const sharedKnowledgeBases = useMemo(() => {
    const start = (pagination.shared.page - 1) * pagination.shared.perPage;
    return sharedKnowledgeBasesAll.slice(start, start + pagination.shared.perPage);
  }, [sharedKnowledgeBasesAll, pagination.shared]);

  const publicKnowledgeBases = useMemo(() => {
    const start = (pagination.public.page - 1) * pagination.public.perPage;
    return publicKnowledgeBasesAll.slice(start, start + pagination.public.perPage);
  }, [publicKnowledgeBasesAll, pagination.public]);

  // Reset pagination when search query changes
  useEffect(() => {
    const perPage = getPerPage();
    setPagination({
      myKb: { page: 1, perPage },
      shared: { page: 1, perPage },
      public: { page: 1, perPage },
    });
  }, [searchQuery]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuOpen && !(event.target as Element).closest('.action-menu-container')) {
        setActionMenuOpen(null);
      }
    };

    if (actionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [actionMenuOpen]);

  // Close sort dropdown when clicking outside (ref-based pattern, matches FileListPanel)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortDropdownOpen(false);
      }
    };

    if (sortDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sortDropdownOpen]);

  // Update pagination on screen width change
  useEffect(() => {
    const handleResize = () => {
      const perPage = getPerPage();
      setPagination({
        myKb: { page: 1, perPage },
        shared: { page: 1, perPage },
        public: { page: 1, perPage },
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to get section-specific styles
  const getSectionStyles = (sectionKey: 'myKb' | 'shared' | 'public') => {
    const styles = {
      myKb: {
        iconBg: 'bg-gradient-to-br from-blue-50 to-blue-100',
        iconColor: 'text-blue-600',
        borderColor: 'border-blue-200',
        cardBg: 'bg-white',
      },
      shared: {
        iconBg: 'bg-gradient-to-br from-purple-50 to-purple-100',
        iconColor: 'text-purple-600',
        borderColor: 'border-purple-200',
        cardBg: 'bg-white',
      },
      public: {
        iconBg: 'bg-gradient-to-br from-green-50 to-green-100',
        iconColor: 'text-green-600',
        borderColor: 'border-green-200',
        cardBg: 'bg-white',
      },
    };
    return styles[sectionKey];
  };

  // Helper function to normalize icon name to PascalCase
  const normalizeIconName = (iconName: string): string => {
    if (!iconName) return iconName;
    // If already PascalCase (starts with uppercase), return as-is
    if (/^[A-Z]/.test(iconName)) return iconName;
    // Handle kebab-case (e.g., flask-conical -> FlaskConical)
    if (iconName.includes('-')) {
      return iconName
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('');
    }
    // Convert camelCase or lowercase to PascalCase
    return iconName.charAt(0).toUpperCase() + iconName.slice(1);
  };

  // Helper function to get icon component from knowledgebase metadata
  const getKnowledgeBaseIcon = (
    kb: KnowledgeBase
  ): React.ComponentType<{ size?: number; className?: string }> => {
    // Check for custom cover first (new API)
    const coverUrl = kb.meta_data?.cover_url;
    if (coverUrl) {
      // Return a cover component that matches the IconComponent interface
      return () => (
        <img
          src={fileAPI.getCoverUrl(coverUrl)}
          alt='Knowledge base cover'
          className='w-full h-full object-cover'
        />
      );
    }

    // Check for legacy logo URL (backward compatibility)
    const logoUrl = kb.meta_data?.logo_url;
    if (logoUrl) {
      // Return a logo component that matches the IconComponent interface
      return () => (
        <img src={logoUrl} alt='Knowledge base logo' className='w-full h-full object-cover' />
      );
    }

    // Fallback to predefined icon
    const iconName = kb.meta_data?.icon;
    if (!iconName) {
      // Fallback to section-specific icon
      return FileText;
    }
    // Normalize icon name to PascalCase
    const normalizedName = normalizeIconName(iconName);
    const IconComponent = Icons[normalizedName as keyof typeof Icons] as React.ComponentType<{
      size?: number;
      className?: string;
    }>;
    if (!IconComponent) {
      // Fallback to section-specific icon
      return FileText;
    }
    return IconComponent;
  };

  // Skeleton Card Component for loading state
  const SkeletonCard: React.FC<{ sectionKey: 'myKb' | 'shared' | 'public' }> = ({ sectionKey }) => {
    const sectionStyles = getSectionStyles(sectionKey);
    const SectionIcon = sectionKey === 'myKb' ? FileText : sectionKey === 'shared' ? Users : Globe;

    return (
      <div
        className={`bg-white border-gray-200 border rounded-2xl overflow-hidden`}
        role='status'
        aria-label='Loading knowledge base'
      >
        {/* Top Section - Logo/Image Area */}
        <div className='relative w-full h-36 sm:h-40 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center'>
          <div
            className={`w-20 h-20 ${sectionStyles.iconBg} rounded-2xl flex items-center justify-center`}
          >
            <SectionIcon className={sectionStyles.iconColor} size={40} />
          </div>
          {/* Status badges skeleton */}
          <div className='absolute top-3 left-3 flex gap-1.5'>
            <div className='w-16 h-6 bg-white/60 rounded-full' />
            <div className='w-12 h-6 bg-white/60 rounded-full' />
          </div>
          {/* Action button skeleton */}
          <div className='absolute top-3 right-3 w-10 h-10 bg-white/60 rounded-xl' />
        </div>

        {/* Bottom Section - Content */}
        <div className='p-4'>
          {/* Title */}
          <div className='h-6 bg-gray-200 rounded w-3/4 mb-2' />

          {/* Description */}
          <div className='h-4 bg-gray-200 rounded w-full mb-2' />
          <div className='h-4 bg-gray-200 rounded w-2/3 mb-4' />

          {/* Metadata */}
          <div className='flex items-center justify-between pt-3 border-t border-gray-100'>
            <div className='flex items-center gap-4'>
              <div className='w-16 h-4 bg-gray-200 rounded' />
              <div className='w-20 h-4 bg-gray-200 rounded' />
            </div>
            <div className='w-20 h-4 bg-gray-200 rounded' />
          </div>
        </div>
      </div>
    );
  };

  // Component to render a single knowledge base card (vertical design with top image/logo)
  const CompactKBCard: React.FC<{
    kb: KnowledgeBase;
    sectionKey: 'myKb' | 'shared' | 'public';
  }> = ({ kb, sectionKey }) => {
    const sectionStyles = getSectionStyles(sectionKey);
    const isInactive = !kb.is_active;
    const KBIcon = getKnowledgeBaseIcon(kb);

    // Get cover image URL (new API) or legacy logo URL
    const coverUrl = kb.meta_data?.cover_url && fileAPI.getCoverUrl(kb.meta_data.cover_url);
    const hasImageCover = coverUrl || kb.meta_data?.logo_url;

    // Picasso background styles for icon covers
    const getPICassoBgClass = (sectionKey: 'myKb' | 'shared' | 'public') => {
      const picassoClasses = {
        myKb: 'picasso-bg-blue',
        shared: 'picasso-bg-purple',
        public: 'picasso-bg-green',
      };
      return picassoClasses[sectionKey];
    };

    return (
      <div
        data-testid='kb-card'
        className={`action-menu-container ${sectionStyles.cardBg} ${
          isInactive ? 'border-gray-300 opacity-70' : sectionStyles.borderColor
        } border rounded-xl overflow-hidden ${
          isInactive ? 'hover:shadow-md' : 'hover:shadow-lg'
        } transition-all duration-200 cursor-pointer touch-manipulation group relative flex flex-col w-full min-w-0`}
        onClick={() => handleKnowledgeBaseClick(kb.id)}
        role='article'
        aria-label={`Knowledge base: ${kb.name}, ${isInactive ? 'Inactive' : 'Active'}`}
      >
        {/* Top half - Cover/Image Area */}
        <div className='relative' style={{ height: '112px' }}>
          {hasImageCover ? (
            <ProgressiveCoverImage
              src={coverUrl || kb.meta_data?.logo_url || ''}
              alt={`${kb.name} cover`}
              picassoClass={getPICassoBgClass(sectionKey)}
            />
          ) : (
            <div
              className={`relative w-full h-full flex items-center justify-center overflow-hidden ${getPICassoBgClass(sectionKey)}`}
            >
              {/* Picasso art layer for icon covers */}
              <div className='picasso-art-layer' />
              <div className='picasso-art-fragment-1' />
              <div className='picasso-art-fragment-2' />
              {/* Enhanced icon with gradient and larger size */}
              <div
                className={`kb-icon-enhanced ${sectionKey === 'myKb' ? 'my-kb' : sectionKey === 'shared' ? 'shared' : 'public'}`}
              >
                <KBIcon size={48} />
              </div>
            </div>
          )}

          {/* Status badges overlay at top-right of image area */}
          <div className='absolute top-2 left-2 flex flex-wrap items-center gap-1 pointer-events-none'>
            {isInactive && (
              <span className='text-xs bg-white/90 backdrop-blur-sm text-gray-600 px-2 py-0.5 rounded-full font-medium shadow-sm'>
                {t('knowledge.status.inactive')}
              </span>
            )}
            {kb.is_default && (
              <span className='text-xs bg-orange-500/90 backdrop-blur-sm text-white px-2 py-0.5 rounded-full font-medium shadow-sm'>
                {t('knowledge.default')}
              </span>
            )}
          </div>
        </div>

        {/* Bottom half - Content */}
        <div className='p-3 flex-1 flex flex-col justify-between'>
          {/* Title and description */}
          <div className='mb-2'>
            <div className='flex items-center gap-1.5'>
              <h3 className='font-semibold text-gray-900 text-sm truncate group-hover:text-blue-600 transition-colors'>
                {kb.name}
              </h3>
            </div>
            <p className='text-xs text-gray-500 truncate mt-0.5'>
              {kb.description || t('knowledge.noDescription')}
            </p>
          </div>

          {/* Metadata */}
          <div className='flex items-center justify-between text-xs text-gray-500 relative'>
            <div className='flex items-center gap-2'>
              <span className='flex items-center gap-1'>
                <FileText size={12} />
                <span className='font-medium'>{kb.document_count}</span>
              </span>
              {sectionKey === 'myKb' && (
                <span
                  className='flex items-center gap-1'
                  title={
                    kb.is_public
                      ? t('knowledge.publicTooltip')
                      : t('knowledge.sharedWithCount', { count: getShareCount(kb) })
                  }
                >
                  {kb.is_public ? <Globe size={12} /> : <UserPlus size={12} />}
                  <span className='font-medium'>
                    {kb.is_public ? t('knowledge.publicLabel') : getShareCount(kb)}
                  </span>
                </span>
              )}
              {sectionKey === 'public' && kb.owner && (
                <span
                  className='flex items-center gap-1'
                  title={kb.owner.full_name || kb.owner.email}
                >
                  <Users size={12} />
                  <span className='truncate max-w-[60px]'>
                    {kb.owner.full_name || kb.owner.email}
                  </span>
                </span>
              )}
              {sectionKey === 'shared' && kb.owner && (
                <span
                  className='flex items-center gap-1'
                  title={kb.owner.full_name || kb.owner.email}
                >
                  <Users size={12} />
                  <span className='truncate max-w-[60px]'>
                    {kb.owner.full_name || kb.owner.email}
                  </span>
                </span>
              )}
              <span className='text-gray-400 whitespace-nowrap'>
                {formatDateWithYear(kb.updated_at)}
              </span>
            </div>

            {/* Action menu button - only visible on hover */}
            <div className='relative'>
              <button
                className='opacity-0 group-hover:opacity-100 group-hover:visible invisible p-1 hover:bg-gray-200 rounded-lg transition-all duration-200'
                onClick={e => {
                  e.stopPropagation();
                  setActionMenuOpen(actionMenuOpen === kb.id ? null : kb.id);
                }}
                aria-label={`Actions for ${kb.name}`}
              >
                <MoreVertical size={14} className='text-gray-400' />
              </button>

              {/* Action Menu Dropdown */}
              {actionMenuOpen === kb.id && (
                <div
                  className='absolute bottom-full right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px] z-20'
                  onClick={e => e.stopPropagation()}
                >
                  {sectionKey === 'myKb' && (
                    <>
                      {isInactive ? (
                        <button
                          className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-green-600'
                          onClick={() => {
                            handleActivateKB(kb.id);
                            setActionMenuOpen(null);
                          }}
                        >
                          <Power size={14} />
                          {t('knowledge.actions.activate')}
                        </button>
                      ) : (
                        <>
                          {canShareKnowledgeBase(kb, user?.id) && (
                            <button
                              className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                              onClick={() => {
                                setKBToShare(kb);
                                setShowShareModal(true);
                                setActionMenuOpen(null);
                              }}
                            >
                              <Share2 size={14} />
                              {t('knowledge.share.memberManagement')}
                            </button>
                          )}
                          {canShareKnowledgeBase(kb, user?.id) && (
                            <button
                              className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                              onClick={() => handleEditKnowledgeBase(kb)}
                            >
                              <Edit size={14} />
                              {t('knowledge.actions.edit')}
                            </button>
                          )}
                          <div className='border-t border-gray-100 my-1' />
                          <button
                            className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-600'
                            onClick={() => {
                              handleDeactivateKB(kb.id);
                              setActionMenuOpen(null);
                            }}
                          >
                            <PowerOff size={14} />
                            {t('knowledge.actions.deactivate')}
                          </button>
                          {canShareKnowledgeBase(kb, user?.id) && (
                            <>
                              <div className='border-t border-gray-100 my-1' />
                              <button
                                className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-red-600'
                                onClick={() => handleDeleteClick(kb)}
                              >
                                <Trash2 size={14} />
                                {t('knowledge.actions.delete')}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {sectionKey === 'public' && (
                    <>
                      {isInactive && kb.owner_id === user?.id ? (
                        <button
                          className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-green-600'
                          onClick={() => {
                            handleActivateKB(kb.id);
                            setActionMenuOpen(null);
                          }}
                        >
                          <Power size={14} />
                          {t('knowledge.actions.activate')}
                        </button>
                      ) : (
                        <button
                          className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                          onClick={() => {
                            navigate(`/knowledge/${kb.id}/files`);
                            setActionMenuOpen(null);
                          }}
                        >
                          <Eye size={14} />
                          {t('knowledge.actions.viewDetails')}
                        </button>
                      )}
                    </>
                  )}
                  {sectionKey === 'shared' && (
                    <>
                      {isInactive && kb.owner_id === user?.id ? (
                        <button
                          className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-green-600'
                          onClick={() => {
                            handleActivateKB(kb.id);
                            setActionMenuOpen(null);
                          }}
                        >
                          <Power size={14} />
                          {t('knowledge.actions.activate')}
                        </button>
                      ) : (
                        <button
                          className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                          onClick={() => {
                            navigate(`/knowledge/${kb.id}/files`);
                            setActionMenuOpen(null);
                          }}
                        >
                          <Eye size={14} />
                          {t('knowledge.actions.viewDetails')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Component to render a single knowledge base table row
  const KBTableRow: React.FC<{
    kb: KnowledgeBase;
    sectionKey: 'myKb' | 'shared' | 'public';
  }> = ({ kb, sectionKey }) => {
    const sectionStyles = getSectionStyles(sectionKey);
    const isInactive = !kb.is_active;
    const KBIcon = getKnowledgeBaseIcon(kb);

    // Get cover image URL (new API) or legacy logo URL
    const coverUrl = kb.meta_data?.cover_url && fileAPI.getCoverUrl(kb.meta_data.cover_url);
    const hasImageCover = coverUrl || kb.meta_data?.logo_url;

    // Picasso background styles for icon covers
    const getPICassoBgClass = (sectionKey: 'myKb' | 'shared' | 'public') => {
      const picassoClasses = {
        myKb: 'picasso-bg-blue',
        shared: 'picasso-bg-purple',
        public: 'picasso-bg-green',
      };
      return picassoClasses[sectionKey];
    };

    return (
      <tr
        data-testid='kb-row'
        className={`action-menu-container group hover:bg-gray-50 transition-colors ${
          isInactive ? 'opacity-70' : ''
        }`}
        onClick={() => handleKnowledgeBaseClick(kb.id)}
      >
        <td className='px-4 py-3'>
          <div className='flex items-center gap-3'>
            {/* Cover/Icon */}
            {hasImageCover ? (
              <div className='w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden'>
                <ProgressiveCoverImage
                  src={coverUrl || kb.meta_data?.logo_url || ''}
                  alt={`${kb.name} cover`}
                  picassoClass={getPICassoBgClass(sectionKey)}
                />
              </div>
            ) : (
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${getPICassoBgClass(sectionKey)}`}
                style={{ position: 'relative' }}
              >
                {/* Picasso art layer for icon covers (table version) */}
                <div className='picasso-art-layer' />
                <div className='picasso-art-fragment-1' />
                <div className='picasso-art-fragment-2' />
                <div className='w-full h-full bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center'>
                  <KBIcon className={sectionStyles.iconColor} size={20} />
                </div>
              </div>
            )}
            {/* Name and description */}
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-1.5'>
                <span className='font-medium text-gray-900 truncate text-sm'>{kb.name}</span>
              </div>
              <div className='text-xs text-gray-500 truncate'>
                {kb.description || t('knowledge.noDescription')}
              </div>
            </div>
          </div>
        </td>
        <td className='px-4 py-3 text-sm text-gray-600'>
          <span className='flex items-center gap-1.5'>
            <FileText size={14} />
            {kb.document_count}
          </span>
        </td>
        {sectionKey === 'myKb' && (
          <td className='px-4 py-3 text-sm text-gray-600'>
            <span
              className='flex items-center gap-1.5'
              title={
                kb.is_public
                  ? t('knowledge.publicTooltip')
                  : t('knowledge.sharedWithCount', { count: getShareCount(kb) })
              }
            >
              {kb.is_public ? <Globe size={14} /> : <UserPlus size={14} />}
              {kb.is_public ? t('knowledge.publicLabel') : getShareCount(kb)}
            </span>
          </td>
        )}
        {sectionKey === 'public' && (
          <td className='px-4 py-3 text-sm text-gray-600'>
            <span
              className='flex items-center gap-1.5'
              title={kb.owner?.full_name || kb.owner?.email}
            >
              <Users size={14} />
              <span className='truncate max-w-[120px]'>
                {kb.owner?.full_name || kb.owner?.email || '-'}
              </span>
            </span>
          </td>
        )}
        {sectionKey === 'shared' && (
          <td className='px-4 py-3 text-sm text-gray-600'>
            <span
              className='flex items-center gap-1.5'
              title={kb.owner?.full_name || kb.owner?.email}
            >
              <Users size={14} />
              <span className='truncate max-w-[120px]'>
                {kb.owner?.full_name || kb.owner?.email || '-'}
              </span>
            </span>
          </td>
        )}
        <td className='px-4 py-3 text-sm text-gray-500 whitespace-nowrap'>
          {formatDateWithYear(kb.updated_at)}
        </td>
        <td className='px-4 py-3'>
          <div className='flex items-center gap-1.5'>
            {/* Status badges */}
            {isInactive ? (
              <span className='text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium'>
                {t('knowledge.status.inactive')}
              </span>
            ) : (
              <span className='text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium'>
                {t('knowledge.status.active')}
              </span>
            )}
            {kb.is_default && (
              <span className='text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium'>
                {t('knowledge.default')}
              </span>
            )}
          </div>
        </td>
        <td className='px-4 py-3 text-right relative'>
          {/* Action menu */}
          <div className='relative inline-block'>
            <button
              className='p-1.5 hover:bg-gray-200 rounded-lg transition-colors'
              onClick={e => {
                e.stopPropagation();
                setActionMenuOpen(actionMenuOpen === kb.id ? null : kb.id);
              }}
            >
              <MoreVertical size={14} className='text-gray-400' />
            </button>

            {/* Action Menu Dropdown */}
            {actionMenuOpen === kb.id && (
              <div
                className='absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px] z-50'
                onClick={e => e.stopPropagation()}
              >
                {sectionKey === 'myKb' && (
                  <>
                    {isInactive ? (
                      <button
                        className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-green-600'
                        onClick={() => {
                          handleActivateKB(kb.id);
                          setActionMenuOpen(null);
                        }}
                      >
                        <Power size={14} />
                        {t('knowledge.actions.activate')}
                      </button>
                    ) : (
                      <>
                        {canShareKnowledgeBase(kb, user?.id) && (
                          <button
                            className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                            onClick={() => {
                              setKBToShare(kb);
                              setShowShareModal(true);
                              setActionMenuOpen(null);
                            }}
                          >
                            <Share2 size={14} />
                            {t('knowledge.share.memberManagement')}
                          </button>
                        )}
                        {canShareKnowledgeBase(kb, user?.id) && (
                          <button
                            className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                            onClick={() => handleEditKnowledgeBase(kb)}
                          >
                            <Edit size={14} />
                            {t('knowledge.actions.edit')}
                          </button>
                        )}
                        <div className='border-t border-gray-100 my-1' />
                        <button
                          className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-600'
                          onClick={() => {
                            handleDeactivateKB(kb.id);
                            setActionMenuOpen(null);
                          }}
                        >
                          <PowerOff size={14} />
                          {t('knowledge.actions.deactivate')}
                        </button>
                        {canShareKnowledgeBase(kb, user?.id) && (
                          <>
                            <div className='border-t border-gray-100 my-1' />
                            <button
                              className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-red-600'
                              onClick={() => handleDeleteClick(kb)}
                            >
                              <Trash2 size={14} />
                              {t('knowledge.actions.delete')}
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                {sectionKey === 'public' && (
                  <>
                    {isInactive && kb.owner_id === user?.id ? (
                      <button
                        className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-green-600'
                        onClick={() => {
                          handleActivateKB(kb.id);
                          setActionMenuOpen(null);
                        }}
                      >
                        <Power size={14} />
                        {t('knowledge.actions.activate')}
                      </button>
                    ) : (
                      <button
                        className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                        onClick={() => {
                          navigate(`/knowledge/${kb.id}/files`);
                          setActionMenuOpen(null);
                        }}
                      >
                        <Eye size={14} />
                        {t('knowledge.actions.viewDetails')}
                      </button>
                    )}
                  </>
                )}
                {sectionKey === 'shared' && (
                  <>
                    {isInactive && kb.owner_id === user?.id ? (
                      <button
                        className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-green-600'
                        onClick={() => {
                          handleActivateKB(kb.id);
                          setActionMenuOpen(null);
                        }}
                      >
                        <Power size={14} />
                        {t('knowledge.actions.activate')}
                      </button>
                    ) : (
                      <button
                        className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                        onClick={() => {
                          navigate(`/knowledge/${kb.id}/files`);
                          setActionMenuOpen(null);
                        }}
                      >
                        <Eye size={14} />
                        {t('knowledge.actions.viewDetails')}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Component to render knowledge base list section with pagination
  const KnowledgeBaseSection: React.FC<{
    title: string;
    knowledgeBases: typeof filteredKnowledgeBases;
    totalCount: number;
    sectionKey: 'myKb' | 'shared' | 'public';
    showCount?: boolean;
  }> = ({ title, knowledgeBases, totalCount, sectionKey, showCount = true }) => {
    // Always render the section, even if empty
    // if (totalCount === 0) return null;

    const currentPage = pagination[sectionKey].page;
    const perPage = pagination[sectionKey].perPage;
    const totalPages = Math.ceil(totalCount / perPage);
    const sectionStyles = getSectionStyles(sectionKey);

    const handlePageChange = (newPage: number) => {
      setPagination(prev => ({
        ...prev,
        [sectionKey]: { ...prev[sectionKey], page: newPage },
      }));
      // Smooth scroll to top of content area
      document.querySelector('.knowledge-base-content')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    };

    // Get icon component based on section
    const getSectionIcon = () => {
      switch (sectionKey) {
        case 'myKb':
          return FileText;
        case 'shared':
          return Users;
        case 'public':
          return Globe;
      }
    };

    const SectionIcon = getSectionIcon();

    return (
      <div className='mb-6 sm:mb-8'>
        <div className='flex items-center justify-between mb-3 sm:mb-4'>
          <div className='flex items-center gap-2'>
            <div
              className={`w-10 h-10 ${sectionStyles.iconBg} rounded-lg flex items-center justify-center`}
            >
              <SectionIcon className={sectionStyles.iconColor} size={20} />
            </div>
            <h2 className='text-base sm:text-lg font-semibold text-gray-900'>{title}</h2>
          </div>
          {showCount && (
            <span className='text-xs sm:text-sm bg-gray-100 text-gray-600 px-2 py-1 rounded-md font-medium'>
              {totalCount} {t('knowledge.items')}
            </span>
          )}
        </div>

        {totalCount === 0 ? (
          <div className='bg-white border border-gray-200 rounded-lg p-8 sm:p-12 text-center'>
            <div className='w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <SectionIcon className={sectionStyles.iconColor} size={28} />
            </div>
            <h3 className='text-base sm:text-lg font-medium text-gray-900 mb-2'>
              {t('knowledge.noKnowledgeBases')}
            </h3>
          </div>
        ) : (
          <>
            {/* On mobile, always show card view. On desktop, respect viewMode */}
            {viewMode === 'card' || (typeof window !== 'undefined' && window.innerWidth < 640) ? (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4'>
                {knowledgeBases.map(kb => (
                  <CompactKBCard key={kb.id} kb={kb} sectionKey={sectionKey} />
                ))}
              </div>
            ) : (
              <div className='hidden sm:block bg-white border border-gray-200 rounded-xl overflow-visible'>
                <table className='w-full'>
                  <thead className='bg-gray-50 border-b border-gray-200'>
                    <tr>
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('knowledge.name')}
                      </th>
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('knowledge.documents')}
                      </th>
                      {sectionKey === 'myKb' && (
                        <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          {t('knowledge.shared')}
                        </th>
                      )}
                      {(sectionKey === 'public' || sectionKey === 'shared') && (
                        <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          {t('knowledge.owner')}
                        </th>
                      )}
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('knowledge.updated')}
                      </th>
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('knowledge.statusColumn')}
                      </th>
                      <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {/* Operation column */}
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200'>
                    {knowledgeBases.map(kb => (
                      <KBTableRow key={kb.id} kb={kb} sectionKey={sectionKey} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='mt-4 flex flex-col sm:flex-row items-center justify-between gap-4'>
            <div className='text-xs sm:text-sm text-gray-600'>
              {t('knowledge.showing', {
                start: (currentPage - 1) * perPage + 1,
                end: Math.min(currentPage * perPage, totalCount),
                total: totalCount,
              })}
            </div>
            <div className='flex items-center gap-2'>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1 || state.loading}
                className='flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                aria-label='Previous page'
              >
                <ChevronLeft size={16} />
                <span className='hidden sm:inline'>{t('knowledge.files.previousPage')}</span>
              </button>

              {/* Page Numbers */}
              <div className='flex items-center space-x-1'>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={state.loading}
                      className={`px-3 py-2 min-w-[40px] rounded-lg transition-colors text-sm font-medium ${
                        currentPage === pageNum
                          ? 'bg-blue-500 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      aria-label={`Page ${pageNum}`}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || state.loading}
                className='flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                aria-label='Next page'
              >
                <span className='hidden sm:inline'>{t('knowledge.files.nextPage')}</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='flex h-screen overflow-hidden' style={{ backgroundColor: '#FAFAFA' }}>
      {/* Main Content */}
      <div className='flex-1 flex flex-col min-w-0 overflow-hidden'>
        {/* Header */}
        <div
          className='px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4 flex-shrink-0'
          style={{ backgroundColor: '#FAFAFA' }}
        >
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0'>
            <h1 className='text-lg sm:text-xl font-semibold text-gray-900'>
              {t('knowledge.title')}
            </h1>
            <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3'>
              {/* Search */}
              <div className='relative'>
                <Search
                  className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'
                  size={16}
                />
                <input
                  type='text'
                  placeholder={t('knowledge.searchKnowledgeBase')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className='pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64 md:w-80 text-sm'
                />
              </div>

              {/* New Knowledge Base Button */}
              <button
                data-testid='create-kb-button'
                onClick={() => setCreateDialogOpen(true)}
                className='flex items-center justify-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors touch-manipulation'
              >
                <Plus size={16} />
                <span className='text-sm font-medium'>{t('knowledge.newKnowledgeBase')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Global Filter Bar */}
        <div
          className='px-4 sm:px-6 py-3 border-b border-gray-200'
          style={{ backgroundColor: '#FAFAFA' }}
        >
          <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3'>
            <div className='flex flex-wrap items-center justify-between w-full sm:w-auto gap-2'>
              {/* Filter Pills */}
              <div className='flex flex-wrap items-center gap-2'>
                <span className='text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap'>
                  {t('knowledge.filter.label')}:
                </span>
                {[
                  { value: 'all' as const, label: t('knowledge.filter.all') },
                  { value: 'recent' as const, label: t('knowledge.filter.recent') },
                  { value: 'inactive' as const, label: t('knowledge.filter.inactive') },
                ].map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => handleGlobalFilterChange(filter.value)}
                    className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                      globalFilter === filter.value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Sort Dropdown */}
              <div className='relative z-10' ref={sortDropdownRef}>
                <button
                  className='flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors'
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                >
                  <SortIcon size={16} className='text-gray-600' />
                  <span className='text-gray-600'>
                    {[
                      { value: 'name' as const, label: t('knowledge.sort.name') },
                      { value: 'updated' as const, label: t('knowledge.sort.lastUpdated') },
                      { value: 'created' as const, label: t('knowledge.sort.createdDate') },
                      { value: 'documents' as const, label: t('knowledge.sort.documentCount') },
                    ].find(opt => opt.value === sortField)?.label || 'Sort'}
                  </span>
                </button>

                {/* Dropdown for selecting sort field - right-aligned on mobile */}
                {sortDropdownOpen && (
                  <div className='absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px] z-50'>
                    {[
                      {
                        value: 'name' as const,
                        label: t('knowledge.sort.name'),
                        icon: sortOrder === 'asc' ? ArrowUpAZ : ArrowDownZA,
                      },
                      {
                        value: 'updated' as const,
                        label: t('knowledge.sort.lastUpdated'),
                        icon: sortOrder === 'asc' ? CalendarArrowUp : CalendarArrowDown,
                      },
                      {
                        value: 'created' as const,
                        label: t('knowledge.sort.createdDate'),
                        icon: sortOrder === 'asc' ? CalendarArrowUp : CalendarArrowDown,
                      },
                      {
                        value: 'documents' as const,
                        label: t('knowledge.sort.documentCount'),
                        icon: sortOrder === 'asc' ? ArrowUp01 : ArrowDown10,
                      },
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={e => {
                          e.stopPropagation();
                          handleSortChange(option.value);
                          setSortDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          sortField === option.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                        }`}
                      >
                        <option.icon
                          size={16}
                          className={sortField === option.value ? 'text-blue-600' : 'text-gray-500'}
                        />
                        <span>{option.label}</span>
                        {sortField === option.value && (
                          <Check size={16} className='ml-auto text-blue-600' />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* View Mode Toggle - hidden on mobile */}
            <div className='hidden sm:flex items-center bg-gray-100 rounded-lg p-1'>
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'card'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                aria-label='Card view'
                title='Card view'
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'table'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                aria-label='Table view'
                title='Table view'
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className='knowledge-base-content flex-1 overflow-y-auto p-4 sm:p-6 overflow-x-hidden'>
          {state.loading ? (
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4'>
              {[1, 2, 3].map(i => (
                <SkeletonCard key={`myKb-${i}`} sectionKey='myKb' />
              ))}
              {[1, 2, 3].map(i => (
                <SkeletonCard key={`shared-${i}`} sectionKey='shared' />
              ))}
              {[1, 2, 3].map(i => (
                <SkeletonCard key={`public-${i}`} sectionKey='public' />
              ))}
            </div>
          ) : state.error ? (
            <div className='bg-white border border-gray-200 rounded-lg p-12 text-center'>
              <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <FileText className='text-red-400' size={32} />
              </div>
              <h3 className='text-lg font-medium text-gray-900 mb-2'>
                {t('knowledge.errorLoading')}
              </h3>
              <p className='text-gray-600 mb-4'>{state.error}</p>
              <button
                onClick={fetchKnowledgeBases}
                className='flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors mx-auto'
              >
                <Plus size={18} />
                <span>{t('knowledge.retry')}</span>
              </button>
            </div>
          ) : filteredKnowledgeBases.length === 0 && !searchQuery ? (
            <div className='bg-white border border-gray-200 rounded-lg p-12 text-center'>
              <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <FolderOpen className='text-blue-500' size={32} />
              </div>
              <h3 className='text-lg font-medium text-gray-900 mb-2'>
                {t('knowledge.noKnowledgeBases')}
              </h3>
              <p className='text-gray-600 mb-4'>{t('knowledge.createFirstKnowledgeBase')}</p>
              <button
                data-testid='create-kb-button'
                onClick={() => setCreateDialogOpen(true)}
                className='flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors mx-auto'
              >
                <Plus size={18} />
                <span>{t('knowledge.newKnowledgeBase')}</span>
              </button>
            </div>
          ) : searchQuery && filteredKnowledgeBases.length === 0 ? (
            <div className='bg-white border border-gray-200 rounded-lg p-12 text-center'>
              <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <Search className='text-gray-400' size={32} />
              </div>
              <h3 className='text-lg font-medium text-gray-900 mb-2'>
                {t('knowledge.noSearchResults')}
              </h3>
              <p className='text-gray-600 mb-4'>{t('knowledge.tryDifferentSearch')}</p>
              <button
                onClick={() => setSearchQuery('')}
                className='flex items-center space-x-2 text-blue-500 hover:text-blue-600 mx-auto'
              >
                <span>{t('knowledge.clearSearch')}</span>
              </button>
            </div>
          ) : (
            <div>
              {/* Three Sections Layout */}
              <KnowledgeBaseSection
                title={t('knowledge.sections.myKnowledgeBases')}
                knowledgeBases={myKnowledgeBases}
                totalCount={myKnowledgeBasesAll.length}
                sectionKey='myKb'
              />

              <KnowledgeBaseSection
                title={t('knowledge.sections.sharedWithMe')}
                knowledgeBases={sharedKnowledgeBases}
                totalCount={sharedKnowledgeBasesAll.length}
                sectionKey='shared'
              />

              <KnowledgeBaseSection
                title={t('knowledge.sections.publicKnowledgeBases')}
                knowledgeBases={publicKnowledgeBases}
                totalCount={publicKnowledgeBasesAll.length}
                sectionKey='public'
              />
            </div>
          )}
        </div>

        {/* Activate Confirmation Dialog */}
        {activateDialogOpen && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg shadow-xl max-w-md w-full'>
              <div className='p-6'>
                <div className='flex items-center justify-between mb-4'>
                  <h2 className='text-xl font-semibold text-gray-900'>
                    {t('knowledge.activation.dialogTitle')}
                  </h2>
                  <button
                    onClick={() => setActivateDialogOpen(null)}
                    className='text-gray-400 hover:text-gray-600'
                    aria-label={t('common.close')}
                  >
                    <X size={24} />
                  </button>
                </div>

                <div className='mb-6'>
                  <p className='text-gray-600 mb-2'>{t('knowledge.activation.dialogMessage')}</p>
                  <p className='text-sm font-medium text-gray-900 bg-gray-50 p-3 rounded-lg'>
                    {activateDialogOpen.kbName}
                  </p>
                  <p className='text-xs text-gray-500 mt-2'>
                    {t('knowledge.activation.dialogNote')}
                  </p>
                </div>

                <div className='flex items-center justify-end space-x-3'>
                  <button
                    onClick={() => setActivateDialogOpen(null)}
                    className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => handleActivateKB(activateDialogOpen.kbId)}
                    className='px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2'
                  >
                    <Power size={16} />
                    <span>{t('knowledge.actions.activateAndView')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Knowledge Base Dialog */}
        <CreateKnowledgeBaseDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleCreateKnowledgeBase}
        />

        {/* Share Knowledge Base Modal */}
        {showShareModal && kBToShare && (
          <ShareKnowledgeBaseModal
            open={showShareModal}
            onClose={() => {
              setShowShareModal(false);
              setKBToShare(null);
            }}
            knowledgeBaseId={kBToShare.id}
            knowledgeBaseName={kBToShare.name}
            owner={kBToShare.owner}
            is_public={kBToShare.is_public}
          />
        )}

        {/* Edit Knowledge Base Dialog */}
        <CreateKnowledgeBaseDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setKbToEdit(null);
          }}
          onSuccess={handleEditSuccess}
          knowledgeBase={kbToEdit}
        />

        {/* Delete Confirmation Dialog */}
        {deleteConfirmOpen && kbToDelete && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg shadow-xl max-w-md w-full'>
              <div className='p-6'>
                <div className='flex items-center justify-between mb-4'>
                  <h2 className='text-xl font-semibold text-gray-900'>
                    {t('knowledge.actions.deleteConfirmTitle')}
                  </h2>
                  <button
                    onClick={() => {
                      setDeleteConfirmOpen(false);
                      setKbToDelete(null);
                    }}
                    className='text-gray-400 hover:text-gray-600'
                    aria-label={t('common.close')}
                  >
                    <X size={24} />
                  </button>
                </div>

                <div className='mb-6'>
                  <p className='text-gray-600 mb-2'>
                    {t('knowledge.actions.deleteConfirmMessage')}
                  </p>
                  <p className='text-sm font-medium text-gray-900 bg-gray-50 p-3 rounded-lg'>
                    {kbToDelete.name}
                  </p>
                  <p className='text-xs text-gray-500 mt-2'>
                    {t('knowledge.actions.deleteConfirmNote')}
                  </p>
                </div>

                <div className='flex items-center justify-end space-x-3'>
                  <button
                    onClick={() => {
                      setDeleteConfirmOpen(false);
                      setKbToDelete(null);
                    }}
                    className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className='px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-2'
                  >
                    <Trash2 size={16} />
                    <span>{t('knowledge.actions.deleteConfirmButton')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBasePage;
